import { Controller } from "@hotwired/stimulus"

/**
 * Accommodation date picker — range-selection calendar for check-in / check-out.
 *
 * Uses the same visual style as the dive date picker (bw-cal-grid) but selects
 * a contiguous date range instead of individual dates.  Dive dates passed via
 * the `diveDates` value are highlighted so customers can align their stay.
 *
 * Values:
 *   diveDates (Array) — YYYY-MM-DD strings of selected dive dates (read-only display)
 */
export default class extends Controller {
  static targets = ["monthLabel", "daysGrid", "checkInDisplay", "checkOutDisplay", "rangeDisplay"]
  static values = {
    diveDates: { type: Array, default: [] }
  }

  connect() {
    this.checkIn = null
    this.checkOut = null
    this.today = new Date()
    this.today.setHours(0, 0, 0, 0)
    this.viewYear = this.today.getFullYear()
    this.viewMonth = this.today.getMonth()

    this._navigateToEarliestDiveMonth()
    this.renderCalendar()
  }

  diveDatesValueChanged() {
    // Also navigate to the earliest dive month when dive dates change after
    // connect (e.g., customer modifies dives in Step 1 and the wizard pushes
    // the updated list back into this picker). Otherwise the calendar
    // re-renders highlighting on a month the customer has to scroll to find.
    this._navigateToEarliestDiveMonth()
    this.renderCalendar()
  }

  _navigateToEarliestDiveMonth() {
    if (this.diveDatesValue.length === 0) return
    const earliest = this.diveDatesValue.slice().sort()[0]
    const d = new Date(earliest + "T00:00:00")
    this.viewYear = d.getFullYear()
    this.viewMonth = d.getMonth()
  }

  prevMonth(event) {
    event.preventDefault()
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear-- }
    else { this.viewMonth-- }
    this.renderCalendar()
  }

  nextMonth(event) {
    event.preventDefault()
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++ }
    else { this.viewMonth++ }
    this.renderCalendar()
  }

  selectDate(event) {
    event.preventDefault()
    const dateStr = event.currentTarget.dataset.date
    if (!dateStr) return

    if (!this.checkIn || (this.checkIn && this.checkOut)) {
      // First click or restart: set check-in
      this.checkIn = dateStr
      this.checkOut = null
    } else {
      // Second click: set check-out (ensure order)
      if (dateStr <= this.checkIn) {
        this.checkOut = this.checkIn
        this.checkIn = dateStr
      } else {
        this.checkOut = dateStr
      }
    }

    this.renderCalendar()
    this._updateDisplays()
    this.dispatch("rangeChanged", {
      detail: { checkIn: this.checkIn, checkOut: this.checkOut }
    })
  }

  clear(event) {
    event.preventDefault()
    this.checkIn = null
    this.checkOut = null
    this.renderCalendar()
    this._updateDisplays()
    this.dispatch("rangeChanged", {
      detail: { checkIn: null, checkOut: null }
    })
  }

  // --- Rendering ---

  renderCalendar() {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ]

    if (this.monthLabelTargets[0]) {
      this.monthLabelTargets[0].textContent = `${monthNames[this.viewMonth]} ${this.viewYear}`
    }
    if (this.daysGridTargets[0]) {
      this.daysGridTargets[0].innerHTML = this._buildMonthHtml(this.viewYear, this.viewMonth)
    }

    if (this.monthLabelTargets.length > 1 && this.daysGridTargets.length > 1) {
      const nextMonth = (this.viewMonth + 1) % 12
      const nextYear = this.viewMonth === 11 ? this.viewYear + 1 : this.viewYear
      this.monthLabelTargets[1].textContent = `${monthNames[nextMonth]} ${nextYear}`
      this.daysGridTargets[1].innerHTML = this._buildMonthHtml(nextYear, nextMonth)
    }
  }

  _buildMonthHtml(year, month) {
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startDow = firstDay.getDay()
    const diveDatesSet = new Set(this.diveDatesValue)
    const id = this.identifier

    let html = ""

    for (let i = 0; i < startDow; i++) {
      html += `<div></div>`
    }

    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d)
      const dateStr = this._formatDate(date)
      const isPast = date < this.today
      const isDiveDate = diveDatesSet.has(dateStr)
      const isCheckIn = dateStr === this.checkIn
      const isCheckOut = dateStr === this.checkOut
      const isInRange = this.checkIn && this.checkOut && dateStr > this.checkIn && dateStr < this.checkOut

      let innerClasses = "flex items-center justify-center w-9 h-9 mx-auto rounded-full text-sm font-medium transition-all "
      let wrapperStyle = ""

      if (isPast) {
        innerClasses += "text-gray-300 dark:text-base-600"
        if (isDiveDate) innerClasses += " ring-1 ring-purple-400 ring-opacity-40"
        html += `<div class="py-1 text-center"><span class="${innerClasses}">${d}</span></div>`
      } else if (isCheckIn || isCheckOut) {
        const endpointClass = (this.checkIn && this.checkOut) ? (isCheckIn ? " range-start" : " range-end") : ""
        innerClasses += "bg-blue-600 text-white font-bold ring-2 ring-blue-600 ring-offset-1 dark:ring-offset-base-900"
        if (isDiveDate) innerClasses += " ring-purple-400"
        html += `<button type="button" class="py-1 w-full text-center${endpointClass}" data-action="${id}#selectDate" data-date="${dateStr}"><span class="${innerClasses}">${d}</span></button>`
      } else if (isInRange) {
        innerClasses += "text-white font-medium hover:bg-blue-50 hover:bg-opacity-20 cursor-pointer"
        if (isDiveDate) innerClasses += " ring-2 ring-purple-400"
        html += `<button type="button" class="py-1 w-full text-center in-range" data-action="${id}#selectDate" data-date="${dateStr}"><span class="${innerClasses}">${d}</span></button>`
      } else {
        if (isDiveDate) {
          innerClasses += "text-white font-bold ring-2 ring-purple-400 hover:bg-purple-50 hover:bg-opacity-10 cursor-pointer"
        } else {
          innerClasses += "text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-base-700 cursor-pointer"
        }
        html += `<button type="button" class="py-1 w-full text-center" data-action="${id}#selectDate" data-date="${dateStr}"><span class="${innerClasses}">${d}</span></button>`
      }
    }

    return html
  }

  _updateDisplays() {
    if (this.hasCheckInDisplayTarget) {
      this.checkInDisplayTarget.textContent = this.checkIn
        ? this._formatDatePretty(this.checkIn)
        : "Select date"
    }
    if (this.hasCheckOutDisplayTarget) {
      this.checkOutDisplayTarget.textContent = this.checkOut
        ? this._formatDatePretty(this.checkOut)
        : "Select date"
    }
    if (this.hasRangeDisplayTarget) {
      if (this.checkIn && this.checkOut) {
        const nights = Math.round((new Date(this.checkOut) - new Date(this.checkIn)) / (1000 * 60 * 60 * 24))
        this.rangeDisplayTarget.innerHTML = `<span class="fw-medium" style="color: var(--default-color);">From ${this._formatDatePretty(this.checkIn)} to ${this._formatDatePretty(this.checkOut)}</span> <span style="color: rgba(255,255,255,0.45);">(${nights} night${nights !== 1 ? "s" : ""})</span>`
      } else if (this.checkIn) {
        this.rangeDisplayTarget.innerHTML = `<span class="fw-medium" style="color: var(--default-color);">From ${this._formatDatePretty(this.checkIn)}</span> <span style="color: rgba(255,255,255,0.45);">— select check-out date</span>`
      } else {
        this.rangeDisplayTarget.textContent = "Select check-in and check-out dates"
        this.rangeDisplayTarget.style.color = "rgba(255,255,255,0.5)"
      }
    }
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  _formatDatePretty(dateStr) {
    const date = new Date(dateStr + "T00:00:00")
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
  }
}
